import type { CondPairUnary } from 'lodash';
import { head, get } from 'lodash';
import { cond, stubTrue } from 'lodash/fp';
import type { HandleGetNewCode } from 'src/components/AuthNew/types';

import type { FullClientData } from '@lt/api/dist/types/client';
import { api } from 'src/api';
import { resetPhoneFormat } from 'src/components/AuthNew/utils';
import type {
  ClientDataTuple,
  ConfirmClientAction,
  HandleUpdateClient,
  SetEmailWasChanged,
} from './types';

export const postClientUpdate = (data) => api.postClientUpdate(data);

export const elseStub: CondPairUnary<ClientDataTuple, ClientDataTuple> = [
  stubTrue,
  (clients) => clients,
];

export const isEmailWasChanged = ([
  prevClientData,
  newClientData,
]: ClientDataTuple) => prevClientData.email !== newClientData.email;

export const ifEmailWasChanged =
  (setEmailWasChanged: SetEmailWasChanged) =>
  (openMainConfirmation: () => void) =>
    cond<ClientDataTuple, ClientDataTuple>([
      [
        (clients) => isEmailWasChanged(clients) && !isPhoneWasChanged(clients),
        (clientDataTuple) => {
          setEmailWasChanged(true);
          openMainConfirmation();
          return clientDataTuple;
        },
      ],
      [
        isEmailWasChanged,
        (clientDataTuple) => {
          setEmailWasChanged(true);
          return clientDataTuple;
        },
      ],
      elseStub,
    ]);

export const isNameOrSureNameWasChanged = ([
  prevClientData,
  newClientData,
]: ClientDataTuple) =>
  prevClientData?.name !== newClientData?.name ||
  prevClientData?.surname !== newClientData?.surname;

export const ifNameOrSureNameOrEmailWasChanged =
  (clientAuthToken?: string) =>
  (handleUpdateClient: HandleUpdateClient) =>
  (
    setClientInfo: (client: FullClientData) => void,
  ): CondPairUnary<ClientDataTuple, Promise<ClientDataTuple>> =>
    [
      (client): boolean =>
        isNameOrSureNameWasChanged(client) || isEmailWasChanged(client),
      async ([prevClientData, newClientData]): Promise<ClientDataTuple> => {
        if (!clientAuthToken) {
          throw new Error(`clientAuthToken in not define`);
        }
        const response = await handleUpdateClient({
          client: {
            id: prevClientData?.id,
            name: newClientData?.name,
            surname: newClientData?.surname,
            email: newClientData?.email,
          },
          auth_token: clientAuthToken,
        });

        if (!response.success) {
          throw new Error('client update not success');
        }

        if (response.success) {
          /** обновить клиента в стейте */
          setClientInfo(response.client);
        }

        return [prevClientData, newClientData];
      },
    ];

export const changeNameOrSureNameOrEmail = (
  ifWasChanged: CondPairUnary<ClientDataTuple, Promise<ClientDataTuple>>,
) =>
  cond<ClientDataTuple, ClientDataTuple | Promise<ClientDataTuple>>([
    ifWasChanged,
    elseStub,
  ]);

export const isPhoneWasChanged = ([prevClientData, newClientData]) =>
  resetPhoneFormat(prevClientData.phone) !==
  resetPhoneFormat(newClientData.phone);

export const ifPhoneWasChanged =
  (handleGetNewCode: HandleGetNewCode) =>
  (openConfirmationPhone: () => void) =>
  (saveNewPhone: (phone: string | null) => void) =>
    cond<ClientDataTuple, ClientDataTuple | Promise<ClientDataTuple>>([
      [
        isPhoneWasChanged,
        async ([prevClientData, newClientData]) => {
          saveNewPhone(newClientData?.phone);

          /** @desc запросить код */
          const result = await handleGetNewCode({
            client_id: prevClientData?.id || undefined,
            phone: resetPhoneFormat(newClientData?.phone) || '',
            event: 'sign_up',
          });

          if (!result.success) {
            /** @desc если телефон занят то бросить исключение,
             * чтобы обработать ошибку в форме */
            if (
              head(get(result, ['error', 'response', 'data', 'errors'])) ===
              'otp_phone_taken'
            ) {
              // eslint-disable-next-line no-throw-literal
              throw (result as any).error as Error;
            }
          }

          /** @desc открыть окно подтверждения телефона */
          openConfirmationPhone();

          return [prevClientData, newClientData];
        },
      ],
      elseStub,
    ]);

export const ifPhoneAndEmailNotChanged = (
  goToTourists: ConfirmClientAction,
  openShortForm?: () => void,
) =>
  cond<ClientDataTuple, ClientDataTuple>([
    [
      (client) => !isPhoneWasChanged(client) && !isEmailWasChanged(client),
      ([_, client]) => {
        // go to tourists
        openShortForm?.();
        goToTourists(client);
        return [_, client];
      },
    ],
    elseStub,
  ]);
